import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import SEOHead from '../components/SEOHead';

export default function WebApplicationDevelopmentPage() {
  const router = useRouter();
  
  // English-only page - canonical URL is fixed
  const canonicalUrl = '/web-application-development';

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Web Application Development',
    description: 'Professional web application development services in Lithuania. Custom web applications built with modern technologies for businesses seeking advanced digital solutions.',
    provider: {
      '@type': 'Organization',
      name: 'LB Visible',
      url: 'https://www.lbvisible.com',
      knowsAbout: [
        'Web Application Development',
        'Custom Web Applications',
        'Full-Stack Development',
        'API Development',
        'Database Design',
      ],
    },
    areaServed: {
      '@type': 'Country',
      name: 'Lithuania',
    },
  };

  return (
    <>
      <SEOHead
        title='Web Application Development | LBVisible'
        description='Professional web application development services in Lithuania. Custom web applications built with modern technologies for businesses seeking advanced digital solutions.'
        keywords='web application development, custom web applications, full-stack development, web app development Lithuania, API development'
        canonicalUrl={canonicalUrl}
        lang='en'
        structuredData={structuredData}
      />

      <main className='min-h-screen max-w-4xl mx-auto px-4 sm:px-6 py-12'>
        <article className='prose prose-lg dark:prose-invert max-w-none'>
          <style jsx global>{`
            article h1 {
              font-size: 2.25rem;
              font-weight: 700;
              margin-top: 2rem;
              margin-bottom: 1.5rem;
              line-height: 1.2;
            }
            article h2 {
              font-size: 1.875rem;
              font-weight: 700;
              margin-top: 2rem;
              margin-bottom: 1rem;
              line-height: 1.3;
            }
            article h3 {
              font-size: 1.5rem;
              font-weight: 600;
              margin-top: 1.5rem;
              margin-bottom: 0.75rem;
              line-height: 1.4;
            }
            article p {
              margin-bottom: 1.5rem;
              line-height: 1.75;
            }
          `}</style>
          <h1 className='text-gray-900 dark:text-white'>
            Web Application Development and Programming
          </h1>
          
          <h2 className='text-gray-900 dark:text-white'>
            Why Professional Web Application Development Matters
          </h2>
          <p className='text-gray-700 dark:text-gray-400'>
            Web application development is the process of creating dynamic, interactive applications that run in web browsers. Unlike static websites, web applications provide functionality, interactivity, and data management capabilities that enable businesses to automate processes, manage information, and deliver services online. Professional web application development ensures your application is secure, scalable, and performs efficiently under various conditions.
          </p>

          <h2 className='text-gray-900 dark:text-white'>
            Our Web Application Development Process
          </h2>
          <p className='text-gray-700 dark:text-gray-400'>
            Our web application development process begins with a thorough analysis of your business requirements and technical needs. We design the application architecture, select appropriate technologies, and develop custom solutions tailored to your specific use case. We use modern frameworks and tools to ensure your web application is maintainable, scalable, and future-proof. Throughout development, we focus on security, performance, and user experience.
          </p>

          <h3 className='text-gray-900 dark:text-white'>
            Full-Stack Development
          </h3>
          <p className='text-gray-700 dark:text-gray-400'>
            We provide full-stack web application development services, handling both frontend and backend development. Our expertise includes React, Next.js, Node.js, and various database technologies. We create seamless integrations between different components, ensuring your web application works smoothly and efficiently.
          </p>

          <h3 className='text-gray-900 dark:text-white'>
            API Development and Integration
          </h3>
          <p className='text-gray-700 dark:text-gray-400'>
            We develop custom APIs and integrate third-party services to extend your web application&apos;s functionality. Our API development services ensure secure, efficient data exchange and enable your application to communicate with other systems and services effectively.
          </p>

          <h2 className='text-gray-900 dark:text-white'>
            Why Choose LBVisible for Web Application Development
          </h2>
          <p className='text-gray-700 dark:text-gray-400'>
            At LBVisible, we understand that web application development requires technical expertise, attention to detail, and a deep understanding of business needs. We combine these elements to deliver web applications that not only meet your requirements but also exceed expectations. Our development approach focuses on creating solutions that are maintainable, scalable, and aligned with your business goals.
          </p>

          <p className='text-gray-700 dark:text-gray-400'>
            Our web application development services include custom development, database design, API development, third-party integrations, and ongoing maintenance. We work closely with you throughout the project, ensuring transparency, regular updates, and timely delivery.
          </p>

          <p className='text-gray-700 dark:text-gray-400'>
            Whether you need a customer portal, admin dashboard, e-commerce platform, or any other custom web application, we have the expertise to bring your vision to life. Contact us today to discuss your web application development needs and discover how we can help your business succeed online.
          </p>
        </article>
      </main>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async () => {
  return {
    props: {},
  };
};

